import { useEffect, useState } from 'react';
import {
  useCarouselSlides,
  useCreateSlide,
  useUpdateSlide,
  useDeleteSlide,
  useReorderSlides,
} from '../../../features/homepage-slider/hooks/useCarouselSlides';
import {
  MAX_COLLAGE_ASSETS,
  MAX_SLIDES_PER_CAROUSEL,
  SLIDE_PAGES,
  fieldNumber,
  fieldString,
  type CarouselSlide,
  type CreateCarouselSlidePayload,
  type SlideAssetInput,
  type SlideFields,
  type SlidePage,
  type SlideType,
  type UpdateCarouselSlidePayload,
} from '../../../features/homepage-slider/api/carouselSlidesApi';
import { useMerchant, useMerchants } from '../../../features/merchants/hooks/useMerchants';
import { MediaUploader, type AssetEntry } from '../../../shared/components/MediaUploader';
import Button from '../../../shared/components/Button';
import { useAuthStore } from '../../../features/auth/store';
import { useTenantStore } from '../../../shared/stores/useTenantStore';

// ── Carousel model presentation metadata ────────────────────────────────────
const TYPE_META: Record<SlideType, { label: string; badge: string; description: string }> = {
  ONE_ASSET: {
    label: 'Single asset',
    badge: 'bg-violet-100 text-violet-700',
    description: 'One full-bleed image or video with overlaid text and CTA.',
  },
  COLLAGE: {
    label: 'Collage',
    badge: 'bg-emerald-100 text-emerald-700',
    description: `Up to ${MAX_COLLAGE_ASSETS} assets arranged by a storefront layout.`,
  },
};

/**
 * Legacy layout slugs (retired slider-service styles) the storefront still maps
 * renderers from. Collages carry theirs on `arrangement` (free-form — these are
 * only suggestions); single-asset slides carry theirs solely in fields._styleSlug
 * because full-asset and half-asset both collapsed to ONE_ASSET in the ETL.
 */
const COLLAGE_ARRANGEMENTS = ['full-collage', 'half-collage', 'irregular-full-collage', 'curation-collage'];
const ONE_ASSET_LAYOUTS: { slug: string; label: string }[] = [
  { slug: 'full-asset', label: 'Full width — asset fills the slide' },
  { slug: 'half-asset', label: 'Half width — asset beside a text panel' },
];
const DEFAULT_ONE_ASSET_LAYOUT = 'full-asset';

/** The `fields` carrier keys this form edits; everything else in the Json is preserved verbatim. */
const EDITED_FIELD_KEYS = new Set([
  'headline',
  'subheadline',
  'bodyText',
  'badgeText',
  'overlayOpacity',
  'textPosition',
  'assetPosition',
  '_styleSlug',
]);

const DEFAULT_OVERLAY_OPACITY = 0.3;

const INPUT_CLASS =
  'w-full border border-[#D9D9D9] rounded-[8px] px-3 py-2 text-[13px] text-foreground bg-card focus:outline-none focus:border-[#3F3E42]';
const FIELD_LABEL_CLASS = 'text-[11px] font-[500] text-muted-foreground block mb-1';
const SECTION_LABEL_CLASS = 'text-[12px] font-[600] text-foreground block mb-2';

// ── Date helpers (schedule window travels as ISO-8601; inputs are datetime-local) ──
const isoToLocalInput = (iso: string | null): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const localInputToIso = (value: string): string | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const scheduleStatus = (slide: CarouselSlide): 'scheduled' | 'ended' | null => {
  const now = Date.now();
  if (slide.startsAt && new Date(slide.startsAt).getTime() > now) return 'scheduled';
  if (slide.endsAt && new Date(slide.endsAt).getTime() < now) return 'ended';
  return null;
};

// ── Slide modal (add / edit) ────────────────────────────────────────────────
interface SlideModalProps {
  initialSlide: CarouselSlide | null;
  solutionId: string;
  defaultPage: SlidePage;
  onClose: () => void;
  onSave: (payload: CreateCarouselSlidePayload | UpdateCarouselSlidePayload) => void;
  isSaving: boolean;
}

function SlideModal({ initialSlide, solutionId, defaultPage, onClose, onSave, isSaving }: SlideModalProps) {
  const initialFields = initialSlide?.fields ?? null;

  const [type, setType] = useState<SlideType>(initialSlide?.type ?? 'ONE_ASSET');
  const [arrangement, setArrangement] = useState(initialSlide?.arrangement ?? '');
  const [oneAssetLayout, setOneAssetLayout] = useState(() => {
    const carried = fieldString(initialFields, '_styleSlug');
    return ONE_ASSET_LAYOUTS.some((l) => l.slug === carried) ? carried : DEFAULT_ONE_ASSET_LAYOUT;
  });

  // fields carrier keys
  const [headline, setHeadline] = useState(fieldString(initialFields, 'headline'));
  const [subheadline, setSubheadline] = useState(fieldString(initialFields, 'subheadline'));
  const [bodyText, setBodyText] = useState(fieldString(initialFields, 'bodyText'));
  const [badgeText, setBadgeText] = useState(fieldString(initialFields, 'badgeText'));
  const [overlayOpacity, setOverlayOpacity] = useState(
    () => fieldNumber(initialFields, 'overlayOpacity') ?? DEFAULT_OVERLAY_OPACITY,
  );
  // Only persist overlayOpacity when the slide already carried one or the admin touches the slider.
  const [overlaySet, setOverlaySet] = useState(() => fieldNumber(initialFields, 'overlayOpacity') !== null);
  const [textPosition, setTextPosition] = useState(fieldString(initialFields, 'textPosition'));
  const [assetPosition, setAssetPosition] = useState(fieldString(initialFields, 'assetPosition'));

  // first-class columns
  const [ctaText, setCtaText] = useState(initialSlide?.ctaText ?? '');
  const [ctaLink, setCtaLink] = useState(initialSlide?.ctaLink ?? '');
  const [page, setPage] = useState<SlidePage>(initialSlide?.page ?? defaultPage);
  const [customPageSlug, setCustomPageSlug] = useState(initialSlide?.customPageSlug ?? '');
  const [startsAt, setStartsAt] = useState(isoToLocalInput(initialSlide?.startsAt ?? null));
  const [endsAt, setEndsAt] = useState(isoToLocalInput(initialSlide?.endsAt ?? null));
  const [isActive, setIsActive] = useState(initialSlide?.isActive ?? true);

  const [assets, setAssets] = useState<AssetEntry[]>(
    () =>
      (initialSlide?.assets ?? []).map((a) => ({
        mediaId: a.mediaId,
        url: a.url,
        type: a.type,
        position: a.position,
        productId: a.productId ?? undefined,
        ctaLabel: a.ctaLabel ?? undefined,
        ctaUrl: a.ctaUrl ?? undefined,
      })),
  );

  const maxAssets = type === 'ONE_ASSET' ? 1 : MAX_COLLAGE_ASSETS;

  const handleTypeChange = (next: SlideType) => {
    setType(next);
    // ONE_ASSET renders exactly one slot — keep the first asset, drop the rest.
    if (next === 'ONE_ASSET') setAssets((prev) => prev.slice(0, 1));
  };

  const buildFields = (): SlideFields => {
    const fields: SlideFields = {};
    // Preserve carrier keys this form does not edit (e.g. _origPosition) verbatim.
    for (const [key, value] of Object.entries(initialFields ?? {})) {
      if (!EDITED_FIELD_KEYS.has(key)) fields[key] = value;
    }
    if (headline.trim()) fields.headline = headline.trim();
    if (subheadline.trim()) fields.subheadline = subheadline.trim();
    if (bodyText.trim()) fields.bodyText = bodyText.trim();
    if (badgeText.trim()) fields.badgeText = badgeText.trim();
    if (overlaySet) fields.overlayOpacity = overlayOpacity;
    if (textPosition) fields.textPosition = textPosition;
    if (assetPosition) fields.assetPosition = assetPosition;
    // _styleSlug keeps the storefront's legacy layout mapping lossless: the exact
    // slug for single-asset slides (full-asset vs half-asset both map to
    // ONE_ASSET), the arrangement for collages (mirrors the rab-api write path).
    if (type === 'ONE_ASSET') fields._styleSlug = oneAssetLayout;
    else if (arrangement.trim()) fields._styleSlug = arrangement.trim();
    return fields;
  };

  const readyAssets = assets.filter((a) => !a._previewUrl);

  const buildAssets = (): SlideAssetInput[] =>
    readyAssets.map((a, index) => {
      const input: SlideAssetInput = { type: a.type, url: a.url, position: index };
      if (a.mediaId) input.mediaId = a.mediaId;
      if (a.productId?.trim()) input.productId = a.productId.trim();
      if (a.ctaLabel?.trim()) input.ctaLabel = a.ctaLabel.trim();
      if (a.ctaUrl?.trim()) input.ctaUrl = a.ctaUrl.trim();
      return input;
    });

  const canSave = readyAssets.length > 0 && !isSaving;

  const handleSave = () => {
    if (!canSave) return;
    const fields = buildFields();
    const trimmedArrangement = type === 'COLLAGE' ? arrangement.trim() : '';
    const slug = page === 'CUSTOM' ? customPageSlug.trim() : '';
    const startsAtIso = localInputToIso(startsAt);
    const endsAtIso = localInputToIso(endsAt);

    if (initialSlide) {
      // PATCH — explicit nulls clear the nullable columns.
      const payload: UpdateCarouselSlidePayload = {
        type,
        arrangement: trimmedArrangement || null,
        page,
        customPageSlug: slug || null,
        ctaText: ctaText.trim() || null,
        ctaLink: ctaLink.trim() || null,
        fields,
        isActive,
        startsAt: startsAtIso,
        endsAt: endsAtIso,
        assets: buildAssets(),
      };
      onSave(payload);
      return;
    }

    // POST — tenant travels as the x-tenant-id header; position appends upstream.
    const payload: CreateCarouselSlidePayload = {
      solutionId,
      page,
      type,
      fields,
      isActive,
      assets: buildAssets(),
    };
    if (trimmedArrangement) payload.arrangement = trimmedArrangement;
    if (slug) payload.customPageSlug = slug;
    if (ctaText.trim()) payload.ctaText = ctaText.trim();
    if (ctaLink.trim()) payload.ctaLink = ctaLink.trim();
    if (startsAtIso) payload.startsAt = startsAtIso;
    if (endsAtIso) payload.endsAt = endsAtIso;
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-[16px] w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6]">
          <h2 className="text-[16px] font-[700] text-foreground">{initialSlide ? 'Edit Slide' : 'Add Slide'}</h2>
          <button onClick={onClose} className="p-1.5 rounded-[6px] hover:bg-muted text-muted-foreground">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Slide type */}
          <div>
            <label className={SECTION_LABEL_CLASS}>Slide Type</label>
            <div className="grid grid-cols-2 gap-2">
              {(Object.keys(TYPE_META) as SlideType[]).map((slideType) => (
                <button
                  key={slideType}
                  onClick={() => handleTypeChange(slideType)}
                  className={`text-left p-3 rounded-[10px] border-2 transition-all ${
                    type === slideType ? 'border-[#3F3E42] bg-muted' : 'border-[#E5E7EB] hover:border-[#9B97A1]'
                  }`}
                >
                  <span className={`text-[10px] font-[600] px-2 py-0.5 rounded-full ${TYPE_META[slideType].badge}`}>
                    {TYPE_META[slideType].label}
                  </span>
                  <p className="text-[11px] text-muted-foreground mt-1 leading-tight">{TYPE_META[slideType].description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Layout */}
          {type === 'ONE_ASSET' ? (
            <div>
              <label className={SECTION_LABEL_CLASS}>Layout</label>
              <select value={oneAssetLayout} onChange={(e) => setOneAssetLayout(e.target.value)} className={INPUT_CLASS}>
                {ONE_ASSET_LAYOUTS.map((layout) => (
                  <option key={layout.slug} value={layout.slug}>{layout.label}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className={SECTION_LABEL_CLASS}>Arrangement</label>
              <input
                type="text"
                value={arrangement}
                onChange={(e) => setArrangement(e.target.value)}
                list="collage-arrangements"
                placeholder="e.g. curation-collage"
                className={INPUT_CLASS}
              />
              <datalist id="collage-arrangements">
                {COLLAGE_ARRANGEMENTS.map((slug) => (
                  <option key={slug} value={slug} />
                ))}
              </datalist>
              <p className="text-[11px] text-muted-foreground mt-1">
                Free-form layout key the storefront maps to a collage template.
              </p>
            </div>
          )}

          {/* Content fields (ETL carrier keys) */}
          <div>
            <label className={SECTION_LABEL_CLASS}>Slide Content</label>
            <div className="space-y-3">
              <div>
                <label className={FIELD_LABEL_CLASS}>Headline <span>(120 max)</span></label>
                <input type="text" value={headline} onChange={(e) => setHeadline(e.target.value)} maxLength={120} className={INPUT_CLASS} />
              </div>
              <div>
                <label className={FIELD_LABEL_CLASS}>Subheadline <span>(200 max)</span></label>
                <input type="text" value={subheadline} onChange={(e) => setSubheadline(e.target.value)} maxLength={200} className={INPUT_CLASS} />
              </div>
              <div>
                <label className={FIELD_LABEL_CLASS}>Body Text <span>(400 max)</span></label>
                <textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={3} maxLength={400} className={INPUT_CLASS} />
              </div>
              <div>
                <label className={FIELD_LABEL_CLASS}>Badge Text <span>(30 max)</span></label>
                <input type="text" value={badgeText} onChange={(e) => setBadgeText(e.target.value)} maxLength={30} className={INPUT_CLASS} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-[500] text-muted-foreground">
                    Overlay Opacity{overlaySet ? ` — ${Math.round(overlayOpacity * 100)}%` : ' — not set'}
                  </label>
                  {overlaySet && (
                    <button
                      onClick={() => { setOverlaySet(false); setOverlayOpacity(DEFAULT_OVERLAY_OPACITY); }}
                      className="text-[11px] text-muted-foreground hover:text-foreground underline"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={overlayOpacity}
                  onChange={(e) => { setOverlayOpacity(Number(e.target.value)); setOverlaySet(true); }}
                  className="w-full accent-[#3F3E42]"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={FIELD_LABEL_CLASS}>Text Side</label>
                  <select value={textPosition} onChange={(e) => setTextPosition(e.target.value)} className={INPUT_CLASS}>
                    <option value="">— Not set —</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>Asset Side</label>
                  <select value={assetPosition} onChange={(e) => setAssetPosition(e.target.value)} className={INPUT_CLASS}>
                    <option value="">— Not set —</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div>
            <label className={SECTION_LABEL_CLASS}>Call To Action</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={FIELD_LABEL_CLASS}>CTA Text</label>
                <input type="text" value={ctaText} onChange={(e) => setCtaText(e.target.value)} placeholder="e.g. Shop Now" className={INPUT_CLASS} />
              </div>
              <div>
                <label className={FIELD_LABEL_CLASS}>CTA Link</label>
                <input type="text" value={ctaLink} onChange={(e) => setCtaLink(e.target.value)} placeholder="/collections/summer" className={INPUT_CLASS} />
              </div>
            </div>
          </div>

          {/* Placement & schedule */}
          <div>
            <label className={SECTION_LABEL_CLASS}>Placement &amp; Schedule</label>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={FIELD_LABEL_CLASS}>Page</label>
                  <select value={page} onChange={(e) => setPage(e.target.value as SlidePage)} className={INPUT_CLASS}>
                    {SLIDE_PAGES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                {page === 'CUSTOM' && (
                  <div>
                    <label className={FIELD_LABEL_CLASS}>Custom Page Slug</label>
                    <input
                      type="text"
                      value={customPageSlug}
                      onChange={(e) => setCustomPageSlug(e.target.value)}
                      placeholder="summer-campaign"
                      className={INPUT_CLASS}
                    />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={FIELD_LABEL_CLASS}>Starts At</label>
                  <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className={INPUT_CLASS} />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>Ends At</label>
                  <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className={INPUT_CLASS} />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-4 h-4 accent-[#3F3E42]"
                />
                <span className="text-[13px] text-foreground">Active</span>
              </label>
            </div>
          </div>

          {/* Assets */}
          <div>
            <label className={SECTION_LABEL_CLASS}>
              Assets
              <span className="text-muted-foreground font-[400] ml-1">
                ({type === 'ONE_ASSET' ? 'exactly 1' : `up to ${MAX_COLLAGE_ASSETS}`})
              </span>
            </label>
            <MediaUploader
              assets={assets}
              onChange={setAssets}
              maxAssets={maxAssets}
              solutionId={solutionId}
              showPerAssetFields
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[#F3F4F6]">
          <Button size="sm" variant="outline" onClick={onClose} className="h-[36px]">Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={!canSave} loading={isSaving} className="h-[36px]">
            {initialSlide ? 'Save Changes' : 'Create Slide'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main tab ────────────────────────────────────────────────────────────────
export function HomepageSliderTab() {
  const globalMerchantId = useAuthStore((s) => s.selectedTenantId);
  const setSelectedTenantId = useAuthStore((s) => s.setSelectedTenantId);
  const setActiveMerchant = useTenantStore((s) => s.setActiveMerchant);
  const [localMerchantId, setLocalMerchantId] = useState<string>(globalMerchantId ?? '');
  const [pageFilter, setPageFilter] = useState<SlidePage>('HOME');

  // Follow the global merchant selection (e.g. the TenantGuard selector modal) into this page.
  useEffect(() => {
    if (globalMerchantId) setLocalMerchantId(globalMerchantId);
  }, [globalMerchantId]);

  const { data: merchants = [], isLoading: merchantsLoading } = useMerchants();
  const { data: merchant } = useMerchant(localMerchantId);
  const solutionId = merchant?.solutionId ?? null;

  const { data: slides = [], isLoading } = useCarouselSlides(localMerchantId, solutionId, pageFilter);
  const createSlide = useCreateSlide();
  const updateSlide = useUpdateSlide();
  const deleteSlide = useDeleteSlide();
  const reorderSlides = useReorderSlides(localMerchantId);

  const [showModal, setShowModal] = useState(false);
  const [editingSlide, setEditingSlide] = useState<CarouselSlide | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Carousel writes are tenant-scoped by the x-tenant-id header (ApiFetch reads
  // useAuthStore.selectedTenantId), so this selector — like every merchant
  // switcher (see MerchantSelectorModal) — must set BOTH merchant stores or a
  // write could land on a different tenant than the one shown on screen.
  const handleMerchantChange = (id: string) => {
    setLocalMerchantId(id);
    setSelectedTenantId(id || null);
    setActiveMerchant(id ? merchants.find((m) => m.id === id) ?? null : null);
  };

  const handleSave = async (payload: CreateCarouselSlidePayload | UpdateCarouselSlidePayload) => {
    if (editingSlide) {
      await updateSlide.mutateAsync({ id: editingSlide.id, data: payload as UpdateCarouselSlidePayload });
    } else {
      await createSlide.mutateAsync(payload as CreateCarouselSlidePayload);
    }
    setShowModal(false);
    setEditingSlide(null);
  };

  const handleDelete = async (id: string) => {
    await deleteSlide.mutateAsync(id);
    setDeletingId(null);
  };

  const handleToggleActive = (slide: CarouselSlide) => {
    updateSlide.mutate({ id: slide.id, data: { isActive: !slide.isActive } });
  };

  // Button reorder — swaps two neighbours, then PATCHes the COMPLETE ordered id
  // list for the (tenant, solution, page) group (upstream applies it two-phase).
  const moveSlide = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= slides.length || !solutionId) return;
    const reordered = [...slides];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    reorderSlides.mutate({ solutionId, page: pageFilter, slideIds: reordered.map((s) => s.id) });
  };

  const isSaving = createSlide.isPending || updateSlide.isPending;
  const isReady = !!localMerchantId && !!solutionId;
  const atCap = slides.length >= MAX_SLIDES_PER_CAROUSEL;
  const hasInactive = slides.some((s) => !s.isActive);

  return (
    <>
      {(showModal || editingSlide) && solutionId && (
        <SlideModal
          initialSlide={editingSlide}
          solutionId={solutionId}
          defaultPage={pageFilter}
          onClose={() => { setShowModal(false); setEditingSlide(null); }}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}

      <div className="border border-[#B8B5B6] rounded-[25px] bg-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-[18px] font-[600] text-foreground">Carousel Slides</h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Manage the carousel slides shown on the storefront — a page holds up to {MAX_SLIDES_PER_CAROUSEL} slides.
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => { setEditingSlide(null); setShowModal(true); }}
            disabled={!isReady || atCap}
            title={atCap ? `Carousel is full (${MAX_SLIDES_PER_CAROUSEL} slides max)` : undefined}
            icon={
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M8 3.333v9.334M3.333 8h9.334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            }
            iconPosition="left"
            className="h-[36px]"
          >
            Add Slide
          </Button>
        </div>

        {/* Merchant + page scope */}
        <div className="mb-6 flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[220px] max-w-sm">
            <label className="text-[12px] font-[600] text-foreground block mb-1.5">Merchant</label>
            <select
              value={localMerchantId}
              onChange={(e) => handleMerchantChange(e.target.value)}
              disabled={merchantsLoading}
              className="w-full border border-[#D9D9D9] rounded-[8px] px-3 py-2 text-[13px] text-foreground focus:outline-none focus:border-[#3F3E42] bg-card disabled:opacity-50"
            >
              <option value="">— Select a merchant —</option>
              {merchants.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <div className="w-[160px]">
            <label className="text-[12px] font-[600] text-foreground block mb-1.5">Page</label>
            <select
              value={pageFilter}
              onChange={(e) => setPageFilter(e.target.value as SlidePage)}
              className="w-full border border-[#D9D9D9] rounded-[8px] px-3 py-2 text-[13px] text-foreground focus:outline-none focus:border-[#3F3E42] bg-card"
            >
              {SLIDE_PAGES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </div>

        {atCap && (
          <p className="mb-4 text-[12px] text-amber-600">
            This carousel is full ({MAX_SLIDES_PER_CAROUSEL} slides max). Delete a slide to add another.
          </p>
        )}

        {!localMerchantId ? (
          <div className="text-center py-12 text-muted-foreground text-[14px]">
            Select a merchant above to manage their carousel slides.
          </div>
        ) : !solutionId || isLoading ? (
          <div className="flex justify-center py-12">
            <svg className="animate-spin w-6 h-6" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#E5E7EB" strokeWidth="3" />
              <path d="M12 2a10 10 0 0 1 10 10" stroke="#3F3E42" strokeWidth="3" strokeLinecap="round" />
            </svg>
          </div>
        ) : slides.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-[14px]">
            No slides on the {pageFilter} page yet. Click "Add Slide" to create the first one.
          </div>
        ) : (
          <div className="space-y-3">
            {slides.map((slide, index) => {
              const firstAsset = slide.assets[0];
              const headline = fieldString(slide.fields, 'headline');
              const layoutSlug =
                fieldString(slide.fields, '_styleSlug') || slide.arrangement || '';
              const schedule = scheduleStatus(slide);

              return (
                <div key={slide.id} className="flex items-center gap-4 p-4 border border-[#E5E7EB] rounded-[12px] hover:border-[#9B97A1] transition-colors">
                  {/* Position badge */}
                  <span className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#3F3E42] text-white text-[13px] font-[600]">
                    {index + 1}
                  </span>

                  {/* Thumbnail */}
                  <div className="w-16 h-12 rounded-[8px] overflow-hidden bg-muted flex-shrink-0">
                    {firstAsset ? (
                      firstAsset.type === 'VIDEO' ? (
                        <video src={firstAsset.url} muted className="w-full h-full object-cover" />
                      ) : (
                        <img src={firstAsset.url} alt="" className="w-full h-full object-cover" />
                      )
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
                          <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.5" />
                          <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-[600] px-2 py-0.5 rounded-full ${TYPE_META[slide.type].badge}`}>
                        {TYPE_META[slide.type].label}
                      </span>
                      {layoutSlug && (
                        <span className="text-[10px] font-[600] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                          {layoutSlug}
                        </span>
                      )}
                      {!slide.isActive && (
                        <span className="text-[10px] font-[600] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Inactive</span>
                      )}
                      {schedule === 'scheduled' && (
                        <span className="text-[10px] font-[600] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Scheduled</span>
                      )}
                      {schedule === 'ended' && (
                        <span className="text-[10px] font-[600] px-2 py-0.5 rounded-full bg-red-100 text-red-700">Ended</span>
                      )}
                    </div>
                    {headline && <p className="text-[13px] font-[500] text-foreground mt-1 truncate">{headline}</p>}
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {slide.assets.length} asset{slide.assets.length !== 1 ? 's' : ''}
                      {slide.ctaText ? ` · CTA: ${slide.ctaText}` : ''}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleToggleActive(slide)}
                      disabled={updateSlide.isPending}
                      className={`p-1.5 rounded-[6px] hover:bg-muted disabled:opacity-30 transition-colors ${slide.isActive ? 'text-green-600' : 'text-muted-foreground'}`}
                      title={slide.isActive ? 'Deactivate slide' : 'Activate slide'}
                    >
                      {slide.isActive ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M9.88 5.09A9.5 9.5 0 0 1 12 5c6.5 0 10 7 10 7a13.2 13.2 0 0 1-2.16 2.92M6.06 6.06A13.2 13.2 0 0 0 2 12s3.5 7 10 7a9.5 9.5 0 0 0 3.06-.5M3 3l18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => moveSlide(index, -1)}
                      disabled={index === 0 || reorderSlides.isPending}
                      className="p-1.5 rounded-[6px] hover:bg-muted text-muted-foreground disabled:opacity-30 transition-colors"
                      title="Move up"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M5 15l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <button
                      onClick={() => moveSlide(index, 1)}
                      disabled={index === slides.length - 1 || reorderSlides.isPending}
                      className="p-1.5 rounded-[6px] hover:bg-muted text-muted-foreground disabled:opacity-30 transition-colors"
                      title="Move down"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M19 9l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <button
                      onClick={() => { setEditingSlide(slide); setShowModal(false); }}
                      className="p-1.5 rounded-[6px] hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      title="Edit"
                    >
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path d="M11.333 2a1.886 1.886 0 0 1 2.667 2.667L4.667 14H2v-2.667L11.333 2Z" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    {deletingId === slide.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleDelete(slide.id)}
                          disabled={deleteSlide.isPending}
                          className="px-2 py-1 text-[11px] bg-red-500 text-white rounded-[6px] hover:bg-red-600"
                        >
                          Confirm
                        </button>
                        <button onClick={() => setDeletingId(null)} className="px-2 py-1 text-[11px] border border-[#D9D9D9] rounded-[6px]">
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeletingId(slide.id)}
                        className="p-1.5 rounded-[6px] hover:bg-[#FECDCD] text-muted-foreground hover:text-red-500 transition-colors"
                        title="Delete"
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                          <path d="M2 4h12M5.333 4V2.667h5.334V4M6.667 7.333v4M9.333 7.333v4M3.333 4l.667 9.333h8L12.667 4" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {hasInactive && (
          <p className="mt-4 text-[11px] text-muted-foreground">
            Inactive slides stay hidden on the storefront but remain listed here.
          </p>
        )}
      </div>
    </>
  );
}
